import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, RotateCcw, ShieldAlert, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-only view of soft-deleted tickets. Every ticket the CRM has ever
 * "deleted" lives here and can be restored with one click — the row and its
 * events were never actually removed from Mongo.
 */
export default function TicketRecyclePanel() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/tickets/deleted");
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load deleted tickets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const restore = async (id, num) => {
    if (!window.confirm(`Restore ticket #${num || id}? It will re-appear in the main list and dashboards.`)) return;
    setRestoring((s) => ({ ...s, [id]: true }));
    try {
      await api.post(`/tickets/${id}/restore`);
      toast.success(`Ticket #${num || id} restored`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to restore");
    } finally {
      setRestoring((s) => ({ ...s, [id]: false }));
    }
  };

  return (
    <Card data-testid="ticket-recycle-panel" className="border-slate-200">
      <CardHeader>
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-amber-600" />
          <CardTitle className="text-lg font-heading">Deleted tickets (recycle bin)</CardTitle>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Nothing is ever hard-deleted. Every "deleted" ticket is preserved with its full thread and audit trail here, and can be restored at any time.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="text-center py-8 text-sm text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
            Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-400" data-testid="recycle-empty">
            Recycle bin is empty — no deleted tickets.
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="grid grid-cols-[70px_minmax(180px,2fr)_minmax(140px,1fr)_140px_140px_180px] gap-3 px-4 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <span>#</span>
              <span>Subject</span>
              <span>Customer</span>
              <span>Deleted by</span>
              <span>Deleted at</span>
              <span className="text-right">Action</span>
            </div>
            <div className="divide-y divide-slate-100">
              {rows.map((t) => (
                <div
                  key={t.id}
                  data-testid={`recycle-row-${t.id}`}
                  className="grid grid-cols-[70px_minmax(180px,2fr)_minmax(140px,1fr)_140px_140px_180px] gap-3 px-4 py-2.5 text-sm items-center"
                >
                  <span className="font-mono text-xs text-slate-600">#{t.ticket_number || "—"}</span>
                  <span className="truncate">
                    <button
                      type="button"
                      onClick={() => navigate(`/app/tickets/${t.id}`)}
                      className="text-slate-900 hover:text-blue-700 hover:underline text-left inline-flex items-center gap-1"
                      data-testid={`recycle-open-${t.id}`}
                    >
                      {t.subject}
                      <ExternalLink className="h-3 w-3 opacity-50" />
                    </button>
                  </span>
                  <span className="truncate text-slate-600 text-xs">{t.account_name || "—"}</span>
                  <span className="truncate text-slate-600 text-xs">{t.deleted_by_name || "—"}</span>
                  <span className="text-slate-500 text-xs font-mono">{fmtDate(t.deleted_at)}</span>
                  <div className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => restore(t.id, t.ticket_number)}
                      disabled={!!restoring[t.id]}
                      data-testid={`recycle-restore-${t.id}`}
                      className="h-8"
                    >
                      {restoring[t.id] ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      Restore
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
